import { CssBaseline } from '@mui/material';
import * as Sentry from '@sentry/react';
import { ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import { ThemeProviderWrapper } from 'src/features/appTheme';
import { AuthProvider } from 'src/features/authentication';
import Status500 from 'src/pages/Fallbacks/Status/Status500/Status500';
import { themeNames, useThemeStore } from 'src/store/theme/themeStore';
import { SidebarProvider } from './SidebarContext';
import { AbilityProvider } from './AbilityProvider';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AppProviders component serves as the top-level provider for the application.
 * It wraps the entire application with various context providers and essential
 * components for consistent theming, authentication, routing, and error handling.
 *
 * @component
 * @param {AppProvidersProps} children - The child components to be wrapped by the providers.
 * @return {JSX.Element} The wrapped child components with required providers and error boundaries.
 */
const AppProviders = ({ children }: AppProvidersProps) => {
  const theme = useThemeStore((state) => state.theme);

  // AbilityProvider lives INSIDE AuthProvider — it reads the live user role to
  // build the CASL ability, so it needs the auth hook to be available.
  return (
    <AuthProvider>
      <AbilityProvider>
        <HelmetProvider>
          <SidebarProvider>
            <ThemeProviderWrapper>
              <Sentry.ErrorBoundary fallback={<Status500 resetErrorBoundary={() => window.location.reload()} />}>
                <CssBaseline />
                <ToastContainer theme={theme === themeNames.dark ? 'dark' : 'light'} />
                {children}
              </Sentry.ErrorBoundary>
            </ThemeProviderWrapper>
          </SidebarProvider>
        </HelmetProvider>
      </AbilityProvider>
    </AuthProvider>
  );
};

export default AppProviders;
